import React, { useMemo, useState } from "react";
import type { Notification } from "@meupedaco/shared";
import { PageState } from "../../components/PageState";
import { useAuth } from "../../auth/AuthContext";
import { usePagedQuery } from "../../hooks/usePagedQuery";

export const NotificationsPage: React.FC = () => {
  const { client } = useAuth();
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const {
    rows,
    total,
    loading,
    error,
    page,
    take,
    setPage,
    q,
    setQ,
    reload,
  } = usePagedQuery<Notification>({
    endpoint: "/notifications",
    filters: {
      unreadOnly,
    },
  });

  const paginationText = useMemo(() => {
    if (total === 0) {
      return "0 resultados";
    }
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  const handleMarkRead = async (id: number) => {
    setMessage(null);
    const response = await client.post(`/notifications/${id}/read`, {});
    if (!response.ok) {
      setMessage("Falha ao marcar notificacao.");
      return;
    }
    await reload();
  };

  const handleMarkAll = async () => {
    setMessage(null);
    const response = await client.post("/notifications/read-all", {});
    if (!response.ok) {
      setMessage("Falha ao marcar todas como lidas.");
      return;
    }
    await reload();
  };

  return (
    <div className="container py-4">
      <h1 className="mb-3">Notificacoes</h1>
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <label className="form-label">Busca</label>
              <input
                className="form-control"
                placeholder="Buscar notificacao"
                value={q}
                onChange={(event) => setQ(event.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Filtro</label>
              <select
                className="form-select"
                value={unreadOnly ? "unread" : "all"}
                onChange={(event) => setUnreadOnly(event.target.value === "unread")}
              >
                <option value="unread">Nao lidas</option>
                <option value="all">Todas</option>
              </select>
            </div>
            <div className="col-md-3 text-end">
              <button className="btn btn-outline-primary" onClick={handleMarkAll}>
                Marcar todas como lidas
              </button>
            </div>
          </div>
          {message && <div className="alert alert-danger mt-3">{message}</div>}
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <PageState loading={loading} error={error}>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Titulo</th>
                    <th>Mensagem</th>
                    <th>Data</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr key={item.id}>
                      <td>{item.type}</td>
                      <td>{item.title}</td>
                      <td>{item.message ?? "-"}</td>
                      <td>{item.createdAt}</td>
                      <td>{item.readAt ? "Lida" : "Nao lida"}</td>
                      <td className="text-end">
                        {!item.readAt && (
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleMarkRead(item.id)}
                          >
                            Marcar lida
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        Nenhuma notificacao encontrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </PageState>
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">{paginationText}</small>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage(Math.max(1, page - 1))}
              >
                Anterior
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page * take >= total}
                onClick={() => setPage(page + 1)}
              >
                Proximo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
